import axios from 'axios';
import { ApiKeyAuth } from './auth/apiKeyAuth';

/**
 * Servicio HTTP centralizado y reutilizable.
 *
 * Utiliza Axios con un interceptor que inyecta automáticamente el API key
 * como query param (?api_key=...) en cada petición, manteniendo compatibilidad
 * con el middleware backend existente.
 *
 * Uso:
 *   import { httpClient } from '../../api/httpClient';
 *
 *   // POST con autenticación (por defecto)
 *   const data = await httpClient.post(url, { cedula: '123' });
 *
 *   // GET con autenticación
 *   const data = await httpClient.get(url);
 *
 *   // Sin autenticación
 *   const data = await httpClient.post(url, body, { skipAuth: true });
 */

// ─── Respuesta estandarizada de error ────────────────────────────────────────
const buildErrorResponse = (mensaje = 'Error al Consultar', error = null) => ({
  errorDescripcion: mensaje,
  transaccion: false,
  error,
});

// ─── Instancia Axios ─────────────────────────────────────────────────────────
const axiosInstance = axios.create({
  headers: {
  //  Accept: 'application/json',
//    'Content-Type': 'text/plain', // Mismo comportamiento de fetch para evitar CORS preflight (OPTIONS)
  },
  timeout: 60000, // 60 segundos
});

// ─── Interceptor de Request: inyecta api_key como query param ────────────────
axiosInstance.interceptors.request.use(
  (config) => {
    // Serializamos la data a string para imitar el "body: JSON.stringify(param)" original de fetch
    if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
      config.data = JSON.stringify(config.data);
    }

    // Permite omitir autenticación con { skipAuth: true }
    if (config.skipAuth) {
      delete config.skipAuth;
      return config;
    }

    // Genera el token JWT con TTL de 180 segundos (igual que addApiKey)
    const token = ApiKeyAuth.instance().generateToken({ ttl: 180 });
    const separator = config.url.includes('?') ? '&' : '?';
    config.url = `${config.url}${separator}api_key=${token}`;

    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Interceptor de Response: manejo centralizado de errores ─────────────────
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Error de red (sin respuesta del servidor)
    if (!error.response) {
      console.error('[httpClient] Error de red:', error.message);
      return Promise.resolve({
        data: buildErrorResponse('Error de conexión con el servidor', error),
      });
    }

    // Error del servidor (4xx, 5xx)
    console.error(
      `[httpClient] Error ${error.response.status}:`,
      error.response.data
    );

    return Promise.resolve({
      data: buildErrorResponse(
        `Error ${error.response.status}: ${error.response.statusText}`,
        error
      ),
    });
  }
);

// ─── Métodos HTTP públicos ───────────────────────────────────────────────────

/**
 * Realiza una petición GET.
 * @param {string} url - URL del endpoint
 * @param {object} [config] - Configuración adicional de Axios ({ params, skipAuth, ... })
 * @returns {Promise<object>} Datos de la respuesta
 */
const get = async (url, config = {}) => {
  try {
    const response = await axiosInstance.get(url, config);
    return response.data;
  } catch (error) {
    return buildErrorResponse('Error al Consultar', error);
  }
};

/**
 * Realiza una petición POST.
 * @param {string} url - URL del endpoint
 * @param {object} [data] - Body de la petición
 * @param {object} [config] - Configuración adicional de Axios ({ skipAuth, ... })
 * @returns {Promise<object>} Datos de la respuesta
 */
const post = async (url, data = {}, config = {}) => {
  try {
    const response = await axiosInstance.post(url, data, config);
    return response.data;
  } catch (error) {
    return buildErrorResponse('Error al Consultar', error);
  }
};

/**
 * Realiza una petición PUT.
 * @param {string} url - URL del endpoint
 * @param {object} [data] - Body de la petición
 * @param {object} [config] - Configuración adicional de Axios ({ skipAuth, ... })
 * @returns {Promise<object>} Datos de la respuesta
 */
const put = async (url, data = {}, config = {}) => {
  try {
    const response = await axiosInstance.put(url, data, config);
    return response.data;
  } catch (error) {
    return buildErrorResponse('Error al Consultar', error);
  }
};

/**
 * Realiza una petición PATCH.
 * @param {string} url - URL del endpoint
 * @param {object} [data] - Body de la petición
 * @param {object} [config] - Configuración adicional de Axios ({ skipAuth, ... })
 * @returns {Promise<object>} Datos de la respuesta
 */
const patch = async (url, data = {}, config = {}) => {
  try {
    const response = await axiosInstance.patch(url, data, config);
    return response.data;
  } catch (error) {
    return buildErrorResponse('Error al Consultar', error);
  }
};

/**
 * Realiza una petición DELETE.
 * @param {string} url - URL del endpoint
 * @param {object} [config] - Configuración adicional de Axios ({ data, skipAuth, ... })
 * @returns {Promise<object>} Datos de la respuesta
 */
const del = async (url, config = {}) => {
  try {
    const response = await axiosInstance.delete(url, config);
    return response.data;
  } catch (error) {
    return buildErrorResponse('Error al Consultar', error);
  }
};

// ─── Export ──────────────────────────────────────────────────────────────────

export const httpClient = {
  get,
  post,
  put,
  patch,
  delete: del,
};

// Export de la instancia raw de Axios por si se necesita acceso directo
export { axiosInstance };
